import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import type { DailyGoalResponse } from "@consistency-circle/shared";
import { CheckIcon, PauseIcon, PencilIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { apiClient } from "../../../lib/api";
import type { Decision } from "../types";

interface GoalDecisionCardProps {
  readonly goal: DailyGoalResponse;
  readonly lastWeekPercent: number | null;
  readonly position: number;
  readonly total: number;
  readonly onDecided: (decision: Decision) => void;
}

export function GoalDecisionCard({
  goal,
  lastWeekPercent,
  position,
  total,
  onDecided,
}: GoalDecisionCardProps) {
  const [adjusting, setAdjusting] = useState(false);
  const [times, setTimes] = useState<Map<string, string>>(
    new Map(goal.slots.map((slot) => [slot.id, slot.dueTimeLocal])),
  );

  const patchMutation = useMutation({
    mutationFn: (input: Parameters<typeof apiClient.patchDailyGoal>[1]) =>
      apiClient.patchDailyGoal(goal.id, input),
  });

  const pause = () => {
    patchMutation.mutate(
      { active: false },
      { onSuccess: () => onDecided("PAUSED") },
    );
  };

  const saveAdjustments = () => {
    patchMutation.mutate(
      {
        slots: goal.slots.map((slot) => ({
          id: slot.id,
          name: slot.name,
          dueTimeLocal: times.get(slot.id) ?? slot.dueTimeLocal,
          sortOrder: slot.sortOrder,
          missGraceMinutes: slot.missGraceMinutes,
          recoveryAction: slot.recoveryAction,
          notifySelfOnOpen: slot.notifySelfOnOpen,
          active: true,
        })),
      },
      { onSuccess: () => onDecided("ADJUSTED") },
    );
  };

  return (
    <div className="rounded-2xl border border-border bg-card px-5 py-5 space-y-4">
      <div>
        <p className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground">
          Goal {position} of {total}
        </p>
        <h2 className="text-base font-semibold leading-snug mt-0.5">{goal.name}</h2>
        <p className="text-xs text-muted-foreground tabular-nums mt-0.5">
          {lastWeekPercent === null ? "Not tracked last week" : `${lastWeekPercent}% last week`} ·{" "}
          {goal.slots.length} slot{goal.slots.length !== 1 ? "s" : ""}
        </p>
      </div>

      {patchMutation.isError && (
        <p className="text-sm text-destructive">Failed to save. Try again.</p>
      )}

      {!adjusting ? (
        <div className="flex flex-col gap-2">
          <Button size="lg" disabled={patchMutation.isPending} onClick={() => onDecided("KEEP")}>
            <CheckIcon strokeWidth={3} />
            Keep as is
          </Button>
          <Button
            size="lg"
            variant="secondary"
            disabled={patchMutation.isPending}
            onClick={() => setAdjusting(true)}
          >
            <PencilIcon />
            Adjust times
          </Button>
          <Button
            size="lg"
            variant="ghost"
            disabled={patchMutation.isPending}
            onClick={pause}
            className="text-muted-foreground"
          >
            <PauseIcon />
            Pause for now
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {goal.slots.map((slot) => (
            <div key={slot.id} className="flex items-center justify-between gap-3">
              <span className="text-sm truncate">{slot.name}</span>
              <input
                type="time"
                value={times.get(slot.id) ?? slot.dueTimeLocal}
                onChange={(event) =>
                  setTimes((prev) => new Map(prev).set(slot.id, event.target.value))
                }
                className="h-8 rounded-lg border border-input bg-transparent px-2 text-sm tabular-nums focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 outline-none dark:bg-input/30"
              />
            </div>
          ))}
          <div className="flex items-center justify-between pt-1">
            <button
              onClick={() => setAdjusting(false)}
              disabled={patchMutation.isPending}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-2 rounded-lg hover:bg-muted"
            >
              Back
            </button>
            <Button onClick={saveAdjustments} disabled={patchMutation.isPending}>
              {patchMutation.isPending ? "Saving…" : "Save times"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
